import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Image } from 'antd';
import Markdown from '@/components/ui/Markdown';
import { splitPlainTextWithUrls } from '@utils/chatPlainTextLinkify.util';
import { resolveChatMediaUrl } from '@utils/chatMediaUrl.util';
import { isCodeLikeChatMessage, formatCodeLikeChatMessage } from '@utils/chatMessageCodeLike.util';
import { isChatBodyPlainForDisplay } from '@utils/chatMessagePlainBody.util';

/**
 * react-markdown passes `inline: false` for fenced blocks; `node.parent` is often NOT `pre` on the mdast/hast
 * bridge, so parent-tag checks miss and fenced `code` was wrongly styled as inline (double box, no linkify).
 */
export function isMarkdownFencedCode({ inline, className, node }) {
    if (inline === true) return false;
    if (inline === false) return true;
    const cls =
        typeof className === 'string'
            ? className
            : Array.isArray(className)
              ? className.filter(Boolean).join(' ')
              : '';
    if (/\blanguage-/i.test(cls)) return true;
    const p = node?.parent;
    if (p?.type === 'element' && String(p.tagName || '').toLowerCase() === 'pre') return true;
    return false;
}

/** Flatten react-markdown `code` children (fenced blocks) to a string for URL linkification. */
export function flattenChildrenToString(children) {
    const parts = [];
    const walk = (c) => {
        if (c == null || c === false) return;
        if (typeof c === 'string' || typeof c === 'number') {
            parts.push(String(c));
            return;
        }
        if (Array.isArray(c)) {
            c.forEach(walk);
            return;
        }
        if (React.isValidElement(c)) {
            walk(c.props?.children);
        }
    };
    walk(children);
    return parts.join('');
}

/** Fenced code: one text color on the panel; only URLs pick up link color (Slack / stack-trace style). */
export function FencedCodeWithClickableUrls({ text, dark }) {
    const segs = useMemo(() => splitPlainTextWithUrls(String(text ?? '')), [text]);
    const linkColor = dark ? '#6cb6ff' : '#0969da';
    return segs.map((seg, i) =>
        seg.type === 'link' ? (
            <a
                key={`fcu-${i}-${seg.value}`}
                href={seg.value}
                target="_blank"
                rel="noopener noreferrer"
                draggable={false}
                style={{
                    color: linkColor,
                    fontWeight: 500,
                    textDecoration: 'underline',
                    textUnderlineOffset: 2,
                    cursor: 'pointer',
                    userSelect: 'text',
                    WebkitUserSelect: 'text',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {seg.label ?? seg.value}
            </a>
        ) : (
            <React.Fragment key={`fct-${i}`}>{seg.value}</React.Fragment>
        ),
    );
}

export const ChatMessageLink = styled.a.attrs({ draggable: false })`
    position: relative;
    display: inline;
    color: #1677ff !important;
    font-weight: 600;
    text-decoration: none !important;
    user-select: text;
    -webkit-user-select: text;
    transition: color 0.25s ease;
    &::after {
        content: '';
        position: absolute;
        left: 0;
        bottom: -2px;
        width: 100%;
        height: 2px;
        background: currentColor;
        transform: scaleX(0);
        transform-origin: left;
        transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
    }
    &:hover::after {
        transform: scaleX(1);
    }
`;

const CodePre = styled.pre`
    position: relative;
    user-select: text;
    -webkit-user-select: text;
    code {
        display: block;
        white-space: inherit;
        user-select: text;
        -webkit-user-select: text;
    }
`;

export function CodeBlock({ dark, children, style, className }) {
    const normalizedChildren = useMemo(() => {
        if (!React.isValidElement(children)) return children;
        const childType = children.type;
        const isCodeEl = childType === 'code' || childType === 'CODE';
        if (!isCodeEl) return children;
        const childStyle = {
            display: 'block',
            whiteSpace: 'inherit',
            background: 'transparent',
            padding: 0,
            margin: 0,
            border: 'none',
            borderRadius: 0,
            boxShadow: 'none',
            fontFamily: 'inherit',
            fontSize: 'inherit',
            lineHeight: 'inherit',
            color: 'inherit',
            wordBreak: 'inherit',
            overflowWrap: 'inherit',
            userSelect: 'text',
            WebkitUserSelect: 'text',
            MozUserSelect: 'text',
        };
        return React.cloneElement(children, {
            style: { ...(children.props?.style ?? {}), ...childStyle },
        });
    }, [children]);

    return (
        <CodePre
            data-markdown-fenced
            className={className}
            style={{
                margin: '0 0 8px 0',
                display: 'block',
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
                boxSizing: 'border-box',
                padding: '10px 12px',
                borderRadius: 4,
                border: dark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)',
                boxShadow: 'none',
                alignSelf: 'stretch',
                background: dark ? '#1a1a1d' : '#ebebeb',
                color: dark ? 'rgba(235,235,235,0.88)' : '#1d1c1d',
                fontFamily:
                    'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace',
                fontSize: 12.5,
                lineHeight: 1.45,
                tabSize: 4,
                overflow: 'visible',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',
                textAlign: 'left',
                outline: 'none',
                userSelect: 'text',
                WebkitUserSelect: 'text',
                MozUserSelect: 'text',
                cursor: 'auto',
                ...style,
            }}
        >
            {normalizedChildren}
        </CodePre>
    );
}

const QuoteBlockRoot = styled.blockquote`
    user-select: text;
    -webkit-user-select: text;
    p {
        margin: 0;
    }
    p + p {
        margin-top: 6px;
    }
    ul,
    ol {
        margin: 6px 0 0 18px;
        padding: 0;
    }
    li {
        margin: 0 0 4px 0;
    }
`;

function QuoteBlock({ dark, children, style, ...props }) {
    const accent = dark ? 'rgba(148,163,184,0.65)' : 'rgba(100,116,139,0.65)';
    const borderColor = dark ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.10)';

    return (
        <QuoteBlockRoot
            {...props}
            style={{
                margin: 0,
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
                boxSizing: 'border-box',
                padding: '10px 12px',
                borderRadius: 10,
                border: 'none',
                boxShadow: `inset 0 0 0 1px ${borderColor}`,
                borderLeft: `4px solid ${accent}`,
                background: dark ? 'rgba(255,255,255,0.04)' : 'rgba(2,6,23,0.04)',
                color: dark ? 'rgba(255,255,255,0.88)' : '#0f172a',
                textAlign: 'left',
                userSelect: 'text',
                WebkitUserSelect: 'text',
                MozUserSelect: 'text',
                overflow: 'visible',
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',
                ...style,
            }}
        >
            {children}
        </QuoteBlockRoot>
    );
}

/**
 * @param {boolean} dark
 * @returns {import('react-markdown').Components}
 */
export function buildChatMarkdownComponents(dark) {
    return {
        a({ href, children, node, ...props }) {
            const h = href || '';
            const isMention = h.startsWith('#mention-');
            const isTag = h.startsWith('hashtag://');
            if (isMention || isTag) {
                return (
                    <span
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            padding: '1px 8px',
                            borderRadius: 999,
                            fontSize: 13,
                            fontWeight: 700,
                            lineHeight: 1.4,
                            border: `1px solid ${
                                dark ? 'rgba(139,92,246,0.45)' : 'rgba(139,92,246,0.35)'
                            }`,
                            background: dark ? 'rgba(139,92,246,0.18)' : 'rgba(139,92,246,0.10)',
                            color: dark ? '#e9d5ff' : '#6d28d9',
                            userSelect: 'none',
                            whiteSpace: 'nowrap',
                            maxWidth: '100%',
                        }}
                        title={typeof children === 'string' ? children : undefined}
                        {...props}
                    >
                        {children}
                    </span>
                );
            }
            return (
                <ChatMessageLink href={href} target="_blank" rel="noopener noreferrer" {...props}>
                    {children}
                </ChatMessageLink>
            );
        },
        img({ src, alt, node, ...props }) {
            const resolvedSrc = resolveChatMediaUrl(src);
            return (
                <Image
                    src={resolvedSrc}
                    alt={alt ?? ''}
                    preview
                    draggable={false}
                    style={{ maxWidth: '100%', borderRadius: 8 }}
                    {...props}
                />
            );
        },
        pre({ children, node, ...props }) {
            return (
                <CodeBlock dark={dark} className={props.className}>
                    {children}
                </CodeBlock>
            );
        },
        code({ children, node, className, inline, ...rest }) {
            if (isMarkdownFencedCode({ inline, className, node })) {
                const flat = flattenChildrenToString(children);
                const inner =
                    flat.length > 0 ? <FencedCodeWithClickableUrls text={flat} dark={dark} /> : children;
                return (
                    <code
                        {...rest}
                        className={className}
                        style={{
                            fontFamily:
                                'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace',
                            fontSize: 12.5,
                            background: 'transparent',
                            padding: 0,
                            margin: 0,
                            border: 'none',
                            borderRadius: 0,
                            boxShadow: 'none',
                            color: 'inherit',
                            userSelect: 'text',
                            WebkitUserSelect: 'text',
                            MozUserSelect: 'text',
                        }}
                    >
                        {inner}
                    </code>
                );
            }
            const flatInline = flattenChildrenToString(children);
            const inlineInner =
                flatInline.length > 0 && /\b(https?:\/\/[^\s]+|www\.[^\s]+)/i.test(flatInline) ? (
                    <FencedCodeWithClickableUrls text={flatInline} dark={dark} />
                ) : (
                    children
                );
            return (
                <code
                    {...rest}
                    className={className}
                    style={{
                        fontFamily:
                            'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace',
                        fontSize: '0.92em',
                        lineHeight: 1.35,
                        padding: '2px 6px',
                        borderRadius: 4,
                        background: dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                        border: dark ? '1px solid rgba(255,255,255,0.14)' : '1px solid rgba(0,0,0,0.1)',
                        color: dark ? 'rgba(255,255,255,0.88)' : '#374151',
                        userSelect: 'text',
                        WebkitUserSelect: 'text',
                        MozUserSelect: 'text',
                    }}
                >
                    {inlineInner}
                </code>
            );
        },
        blockquote({ children, node, ...props }) {
            return (
                <QuoteBlock dark={dark} {...props}>
                    {children}
                </QuoteBlock>
            );
        },
    };
}

/**
 * Thread drawer parent preview: same markdown/code rules as {@link ChatMessage} so fenced blocks
 * wrap and grow in height (narrow drawer has no horizontal strip).
 *
 * @param {{ body: string, dark: boolean }} props
 */
export function ChatThreadParentMessageBody({ body, dark }) {
    const codeLike = useMemo(() => isCodeLikeChatMessage(body ?? ''), [body]);

    const codeLikeText = useMemo(
        () => (codeLike ? formatCodeLikeChatMessage(body ?? '') : ''),
        [body, codeLike],
    );

    const isPlainBody = useMemo(() => isChatBodyPlainForDisplay(body), [body]);

    const mdComponents = useMemo(() => buildChatMarkdownComponents(dark), [dark]);

    if (!(body ?? '').trim()) return null;

    return (
        <div
            style={{
                width: '100%',
                minWidth: 0,
                maxWidth: '100%',
                overflowX: 'hidden',
                boxSizing: 'border-box',
                fontSize: 14,
                lineHeight: 1.55,
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                color: dark ? 'rgba(255,255,255,0.88)' : '#1d1c1d',
            }}
        >
            {codeLike ? (
                <div style={{ width: '100%', minWidth: 0 }}>
                    <CodeBlock dark={dark}>
                        <code>
                            <FencedCodeWithClickableUrls text={codeLikeText} dark={dark} />
                        </code>
                    </CodeBlock>
                </div>
            ) : isPlainBody ? (
                <div
                    style={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        width: '100%',
                        minWidth: 0,
                    }}
                >
                    {splitPlainTextWithUrls(String(body ?? '')).map((seg, i) =>
                        seg.type === 'link' ? (
                            <ChatMessageLink
                                key={`tp-u-${i}-${seg.value}`}
                                href={seg.value}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {seg.label ?? seg.value}
                            </ChatMessageLink>
                        ) : (
                            <React.Fragment key={`tp-t-${i}`}>{seg.value}</React.Fragment>
                        ),
                    )}
                </div>
            ) : (
                <Markdown
                    source={body}
                    component="div"
                    style={{
                        width: '100%',
                        minWidth: 0,
                        maxWidth: '100%',
                    }}
                    components={mdComponents}
                />
            )}
        </div>
    );
}
